import { Component } from '@theme/component';

/**
 * Same-day orders stop being offered once it's this hour or later, Nairobi time — "4pm EAT"
 * (moved from the original 3pm in design-reference/Cart.dc.html per a later business
 * decision). This is the single source of truth for the cutoff: the calendar's same-day
 * block, the "afternoon" delivery slot's own cutoff below, and every piece of cutoff copy
 * all derive from or must match this value — see the "afternoon" entry in SLOT_DEFS for why
 * that slot's cutoff is `CUTOFF_HOUR * 60` rather than its own independent hardcoded value.
 */
const CUTOFF_HOUR = 16;

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Delivery time-slot definitions — `cutoffMinutes` is minutes-since-midnight Nairobi time.
 * A slot is only ever "passed" when the *selected* date is today (a future date's slots are
 * always available regardless of what time it is right now).
 *
 * The four delivery WINDOWS themselves (10am–12pm, 12–3pm, 3–6pm, 6–8pm) are fixed clock
 * times, unrelated to the same-day ordering cutoff — moving the cutoff doesn't change when
 * deliveries happen, only how late an order can still be placed today. Each slot's own
 * cutoffMinutes is when THAT slot stops being bookable same-day:
 *   - "morning"/"midday" close at their own start time (10am/12pm) regardless of the overall
 *     cutoff — you can't book a window that's already started, whether the shop-wide cutoff
 *     is 3pm or 4pm.
 *   - "afternoon" (3–6pm) is deliberately tied to CUTOFF_HOUR rather than hardcoded to its own
 *     3pm start time: the overall same-day cutoff has always represented "the last moment you
 *     can still book the last remaining slot", and 3–6pm is that last slot. With the cutoff at
 *     4pm, an order placed at 3:30pm can now book the 3–6pm window (it couldn't before, since
 *     the calendar disabled "today" entirely at 3pm) — this is the intended effect of moving
 *     the cutoff, not a side effect to guard against.
 *   - "evening" (6–8pm) keeps its own fixed 6pm cutoff. It was never actually reachable in
 *     practice before (the calendar closed "today" at 3pm, long before 6pm could matter) and
 *     still isn't now (today closes at 4pm, still well before 6pm) — unchanged either way.
 */
const SLOT_DEFS = [
  { key: 'morning', label: '10:00am – 12:00pm', cutoffMinutes: 10 * 60 },
  { key: 'midday', label: '12:00pm – 3:00pm', cutoffMinutes: 12 * 60 },
  { key: 'afternoon', label: '3:00pm – 6:00pm', cutoffMinutes: CUTOFF_HOUR * 60 },
  { key: 'evening', label: '6:00pm – 8:00pm', cutoffMinutes: 18 * 60 },
];

/**
 * @typedef {{ year: number, month: number, day: number }} CalendarDate
 */

/**
 * Reads the real current date/time in Africa/Nairobi, regardless of the visitor's own device
 * timezone — a customer browsing from outside Kenya must still see the same cutoff behaviour
 * a Nairobi-based customer would. `Intl.DateTimeFormat` with an explicit `timeZone` is the
 * only reliable cross-browser way to do this; `new Date().getHours()` etc. would read the
 * visitor's local clock instead.
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number }}
 */
function getNairobiNow() {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Nairobi',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);

  return {
    year: get('year'),
    month: get('month') - 1, // 0-indexed, to match JS Date month numbering
    day: get('day'),
    hour: get('hour') % 24, // some engines report midnight as "24" when hour12 is false
    minute: get('minute'),
  };
}

/** @param {number} year @param {number} month */
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** @param {number} year @param {number} month */
function firstWeekdayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 1)).getUTCDay();
}

/** @param {number} year @param {number} month @param {number} day */
function isSaturday(year, month, day) {
  return new Date(Date.UTC(year, month, day)).getUTCDay() === 6;
}

/**
 * One-time closed dates on top of the recurring Saturday closure (holidays, year-end
 * blackout days, etc.) — a fixed exclusion list, mapping each 'YYYY-MM-DD' date (month is
 * 1-indexed in the string, unlike the 0-indexed CalendarDate.month used everywhere else in
 * this file) to a reason key used to look up BLOCKED_DATE_TITLES / BLOCKED_DATE_CLICK_NOTICES
 * below, since not every blocked date shows the same notice. To add a future closed date with
 * the plain "we're closed" message, give it reason 'closed'. A date already covered by the
 * Saturday rule (e.g. a Saturday that
 * happens to fall in a blocked range) doesn't need an entry here too — getBlockedDateReason
 * and isSaturday are checked independently wherever dates are disabled, so there's no need
 * to duplicate one rule into the other.
 */
const BLOCKED_DATES = new Map([
  ['2026-12-24', 'closed'],
  ['2026-12-25', 'closed'],
  ['2026-12-27', 'closed'],
  ['2026-12-31', 'closed'],
  ['2027-01-01', 'closed'],
  ['2027-02-11', 'valentine'],
  ['2027-02-12', 'valentine'],
  ['2027-02-13', 'valentine'],
  ['2027-02-14', 'valentine'],
  ['2027-02-15', 'valentine'],
  ['2027-02-16', 'valentine'],
]);

/**
 * Hover-title text (the day cell's native `title` tooltip) per BLOCKED_DATES reason key.
 * The 'valentine' message is a known placeholder — Valentine's ordering process isn't
 * finalized yet — expect this exact wording to be replaced once it is; update it here (and
 * in BLOCKED_DATE_CLICK_NOTICES below) only, no other code needs to change.
 */
const BLOCKED_DATE_TITLES = {
  closed: "We're closed on this date",
  valentine: "Directions on how to order for Valentine's will be shared soon, check back or chat with us.",
};

/** Click-notice text (the dismissible notice box) per BLOCKED_DATES reason key. */
const BLOCKED_DATE_CLICK_NOTICES = {
  closed: "We're closed on this date. Please choose another date.",
  valentine: "Directions on how to order for Valentine's will be shared soon, check back or chat with us.",
};

/**
 * @param {number} year @param {number} month (0-indexed) @param {number} day
 * @returns {string | null} the BLOCKED_DATES reason key, or null if the date isn't blocked
 */
function getBlockedDateReason(year, month, day) {
  const monthStr = String(month + 1).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return BLOCKED_DATES.get(`${year}-${monthStr}-${dayStr}`) ?? null;
}

/**
 * @param {CalendarDate} a
 * @param {CalendarDate} b
 * @returns {number} negative if a < b, 0 if equal, positive if a > b
 */
function compareDate(a, b) {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/**
 * Parses a "Weekday, D Month YYYY" string (the exact format the "Delivery Date" cart
 * attribute is written in, see #formatDate) back into a CalendarDate, for re-hydrating the
 * calendar's visual selection on a fresh page load. Returns null for anything that doesn't
 * match — a blank attribute, or legacy/malformed data — rather than throwing, since this
 * only ever gates whether a nice-to-have highlight renders, never a hard requirement.
 * @param {string} value
 * @returns {CalendarDate | null}
 */
function parsePersistedDate(value) {
  if (!value) return null;

  const commaIndex = value.indexOf(', ');
  if (commaIndex === -1) return null;

  const parts = value.slice(commaIndex + 2).trim().split(' ');
  if (parts.length !== 3) return null;

  const day = Number(parts[0]);
  const month = MONTH_LABELS.indexOf(parts[1]);
  const year = Number(parts[2]);

  if (!Number.isInteger(day) || month === -1 || !Number.isInteger(year)) return null;

  return { year, month, day };
}

/**
 * Matches a persisted "Delivery Time Slot" cart attribute value (a slot's label, e.g.
 * "10:00am – 12:00pm") back to its SLOT_DEFS key, for re-hydrating the slot buttons'
 * visual selection.
 * @param {string} label
 * @returns {string} the matching slot key, or '' if no slot has this label
 */
function findSlotKeyByLabel(label) {
  return SLOT_DEFS.find((slot) => slot.label === label)?.key ?? '';
}

/**
 * Cart-page delivery scheduler (design-reference/Cart.dc.html), built incrementally:
 *   - Stage 1 (done): the calendar widget.
 *   - Stage 2 (done): the four delivery time-slot buttons, wired to whichever date is
 *     selected on the calendar above.
 *   - Stage 3 (this addition): buyer name/WhatsApp + recipient name/phone + delivery
 *     address, all required.
 *   - Stage 4 (this addition): syncs every required field to cart attributes and dispatches
 *     a document-level `delivery-scheduler:completeness-change` event that the cart page's
 *     (and cart drawer's) checkout button react to — see component-cart-items.js.
 *
 * Both the day cells and the slot buttons are generated entirely client-side (there's no
 * reactive Liquid — the grids have to be rebuilt whenever the month or selected date changes),
 * but `on:click` on the generated buttons still works via the shared Component framework's
 * document-level delegated event listener + `closest()` lookup, so no manual
 * `addEventListener` per cell/button is needed.
 *
 * @typedef {object} DeliverySchedulerRefs
 * @property {HTMLElement} monthLabel - Shows "<Month> <Year>" for the currently viewed month.
 * @property {HTMLElement} calendarGrid - Container the day-cell buttons are rendered into.
 * @property {HTMLElement} slotsGrid - Container the four time-slot buttons are rendered into.
 * @property {HTMLInputElement} buyerNameInput - The buyer's own name (required).
 * @property {HTMLInputElement} buyerWhatsappInput - The buyer's own WhatsApp number (required).
 * @property {HTMLInputElement} recipientNameInput - The recipient's name (required).
 * @property {HTMLInputElement} recipientPhoneInput - The recipient's phone number (required).
 * @property {HTMLInputElement} addressInput - The delivery address (required).
 * @property {HTMLButtonElement} [prevMonthButton] - Disabled when the view is already on the current month.
 * @property {HTMLElement} [noticeBox] - The dismissible "why is this disabled" notice.
 * @property {HTMLElement} [noticeText] - The notice's message text.
 * @property {HTMLAnchorElement} [noticeWaLink] - The notice's "Chat with us" WhatsApp link, shown only for the same-day-cutoff case.
 * @property {HTMLElement} [selectedDateLabel] - Shows "Selected: <date>" once a date is picked.
 * @extends Component<DeliverySchedulerRefs>
 */
class DeliverySchedulerComponent extends Component {
  requiredRefs = [
    'monthLabel',
    'calendarGrid',
    'slotsGrid',
    'buyerNameInput',
    'buyerWhatsappInput',
    'recipientNameInput',
    'recipientPhoneInput',
    'addressInput',
  ];

  /** @type {number} */
  #viewYear;

  /** @type {number} */
  #viewMonth;

  /** @type {CalendarDate | null} */
  #selected = null;

  /** @type {CalendarDate} */
  #today;

  /** @type {boolean} */
  #cutoffPassedToday = false;

  /** @type {string} */
  #selectedSlot = '';

  /**
   * The raw "Delivery Date"/"Delivery Time Slot" cart attribute strings already saved from a
   * previous visit, if any — read once at connect and used to re-hydrate #selected/
   * #selectedSlot (see connectedCallback). Kept around afterwards (not just parsed and
   * discarded) for two reasons unrelated to the visual highlight: (a) the checkout gate
   * shouldn't regress to "incomplete" just because the customer edited their phone number
   * without touching the calendar again this session, and (b) re-syncing attributes after an
   * unrelated field edit shouldn't blank these two out (POST to /cart/update.js replaces the
   * whole attributes object, it doesn't merge) — see #isComplete() and #syncAttributes().
   * @type {string}
   */
  #persistedDate = '';

  /** @type {string} */
  #persistedSlot = '';

  /**
   * Whether #persistedDate parses to a real date that's still today or later. Computed once
   * at connect and reused everywhere a "is there already a valid saved date" question comes
   * up — the calendar/month hydration below, and #isComplete()'s gate. A persisted date that
   * has since passed (an abandoned cart from a prior day) counts as NOT usable in both
   * places: it's not pre-selected on the calendar, and it no longer satisfies the checkout
   * gate either — the customer has to pick a fresh date before Checkout re-enables, the same
   * as if nothing had ever been saved.
   * @type {boolean}
   */
  #persistedDateUsable = false;

  /** @type {number | undefined} */
  #attributesSyncTimeout;

  connectedCallback() {
    super.connectedCallback();

    const now = getNairobiNow();
    this.#today = { year: now.year, month: now.month, day: now.day };
    this.#cutoffPassedToday = now.hour * 60 + now.minute >= CUTOFF_HOUR * 60;
    this.#persistedDate = this.dataset.existingDeliveryDate ?? '';
    this.#persistedSlot = this.dataset.existingDeliverySlot ?? '';

    // Re-hydrate the calendar/slot selection visually from previously-saved cart attributes,
    // so a returning customer sees their prior choice instead of a blank state. A persisted
    // date that's already in the past (e.g. an abandoned cart from days ago) is deliberately
    // NOT pre-selected, and the view doesn't jump to its month either — the calendar would
    // render that date as a disabled/past cell anyway, which would visually contradict a
    // "selected" highlight, and opening on a past month would let goToPrevMonth() navigate
    // even earlier (it only blocks going back once the view is already *at* the real current
    // month — it was never designed to guard against starting before that).
    const persistedDateParsed = parsePersistedDate(this.#persistedDate);
    this.#persistedDateUsable = !!persistedDateParsed && compareDate(persistedDateParsed, this.#today) >= 0;

    if (this.#persistedDateUsable) {
      this.#selected = persistedDateParsed;
      this.#viewYear = persistedDateParsed.year;
      this.#viewMonth = persistedDateParsed.month;
    } else {
      this.#viewYear = now.year;
      this.#viewMonth = now.month;
    }

    // Only hydrate the slot's own highlight alongside a genuinely usable date — a slot
    // highlighted with no date selected (and Checkout disabled) reads as a contradiction,
    // not a helpful reminder of a prior choice.
    if (this.#persistedDateUsable && this.#persistedSlot) {
      const slotKey = findSlotKeyByLabel(this.#persistedSlot);
      if (slotKey) this.#selectedSlot = slotKey;
    }

    this.#renderCalendar();
    this.#renderSlots();

    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Refreshes #today/#cutoffPassedToday and repaints when this page is restored from the
   * browser's back/forward cache (bfcache) — a bfcache restore never re-runs connectedCallback,
   * so without this, a customer who backgrounded this tab past midnight or past the 4pm cutoff
   * would keep seeing "now" exactly as it was when the tab was suspended (today's cell still
   * selectable, a since-passed slot still bookable, etc.) until they reloaded. Same event/guard
   * pattern assets/cart-icon.js already needed for its own stale-after-restore state. #selected
   * (the customer's own chosen date/slot) is deliberately left untouched here — only the "what
   * is 'now'" inputs to rendering are refreshed, same values #renderSlots() already re-reads
   * fresh on every call for the same reason.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    const now = getNairobiNow();
    this.#today = { year: now.year, month: now.month, day: now.day };
    this.#cutoffPassedToday = now.hour * 60 + now.minute >= CUTOFF_HOUR * 60;
    this.#renderCalendar();
    this.#renderSlots();
  };

  /**
   * Root cause of the "calendar loads with empty date cells" bug: any AJAX cart update on this
   * page (adding a personalized card, an add-on, changing a quantity, ...) re-renders the whole
   * cart-items-component subtree, which this element lives inside of. The Section Rendering
   * API's morph then finds this component's own subtree changed — its calendarGrid holds the
   * day-button HTML #renderCalendar() wrote in, which never matches the freshly-fetched server
   * HTML (calendarGrid is always empty server-side, see blocks/delivery-scheduler.liquid; only
   * JS ever populates it) — and calls updatedCallback() on it. The base Component class's own
   * updatedCallback() only re-syncs refs, it doesn't know this component needs its own re-paint,
   * so the morph's freshly-swapped-in *empty* calendarGrid was simply left as the final state:
   * the weekday header stays correct (that part IS static, server-rendered, morph leaves it
   * alone), but every date number silently disappeared until something else (e.g. clicking
   * next/prev month) called #renderCalendar() again. This element's own JS state (#viewYear,
   * #selected, etc.) survives a morph untouched — only the DOM content needs repainting from it.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#renderCalendar();
    this.#renderSlots();
  }

  goToPrevMonth() {
    if (this.#isAtCurrentMonth()) return;

    this.#viewMonth -= 1;
    if (this.#viewMonth < 0) {
      this.#viewMonth = 11;
      this.#viewYear -= 1;
    }
    this.#renderCalendar();
  }

  goToNextMonth() {
    this.#viewMonth += 1;
    if (this.#viewMonth > 11) {
      this.#viewMonth = 0;
      this.#viewYear += 1;
    }
    this.#renderCalendar();
  }

  /** @param {PointerEvent & {target: HTMLElement}} event */
  handleDayClick(event) {
    const cell = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-cal-day]'));
    if (!cell) return;

    const isSat = cell.dataset.calSaturday === 'true';
    const blockedReason = cell.dataset.calBlockedReason || null;
    const isPast = cell.dataset.calPast === 'true';
    const todayClosed = cell.dataset.calTodayClosed === 'true';

    if (isSat) {
      this.#showNotice(
        "We're closed on Saturdays. You can choose Friday evening or Sunday morning, and our team will confirm via WhatsApp.",
        false
      );
      return;
    }

    if (blockedReason) {
      this.#showNotice(BLOCKED_DATE_CLICK_NOTICES[blockedReason], false);
      return;
    }

    if (todayClosed) {
      this.#showNotice(
        'Same-day orders close at 4pm. Please select tomorrow or later, or chat with us on WhatsApp to check if we can still accommodate you today.',
        true
      );
      return;
    }

    if (isPast) return;

    this.#selected = {
      year: Number(cell.dataset.calYear),
      month: Number(cell.dataset.calMonth),
      day: Number(cell.dataset.calDay),
    };
    this.#hideNotice();
    this.#renderCalendar();
    this.#renderSlots(); // slot "Passed" states depend on whether the selected date is today

    this.dispatchEvent(
      new CustomEvent('delivery-scheduler:date-change', { bubbles: true, detail: { date: this.#selected } })
    );
    this.#dispatchCompletenessChange();
    this.#syncAttributes(); // date/slot changes sync immediately, no debounce
  }

  /** @param {PointerEvent & {target: HTMLElement}} event */
  handleSlotClick(event) {
    const button = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-slot-key]'));
    if (!button || button.dataset.slotPassed === 'true') return;

    this.#selectedSlot = button.dataset.slotKey ?? '';
    this.#hideNotice();
    this.#renderSlots();

    this.dispatchEvent(
      new CustomEvent('delivery-scheduler:slot-change', { bubbles: true, detail: { slot: this.#selectedSlot } })
    );
    this.#dispatchCompletenessChange();
    this.#syncAttributes();
  }

  dismissNotice() {
    this.#hideNotice();
  }

  /**
   * Buyer name/WhatsApp and recipient name/phone/address share the same handling: recompute
   * completeness immediately (typing "Amara" should be able to flip the gate the instant the
   * last required field is filled, not after a debounce), but debounce the actual
   * cart-attribute network write so every keystroke doesn't fire its own request.
   */
  handleBuyerNameInput() {
    this.#handleGiftDetailsInput();
  }

  handleBuyerWhatsappInput() {
    this.#handleGiftDetailsInput();
  }

  handleRecipientNameInput() {
    this.#handleGiftDetailsInput();
  }

  handleRecipientPhoneInput() {
    this.#handleGiftDetailsInput();
  }

  handleAddressInput() {
    this.#handleGiftDetailsInput();
  }

  #handleGiftDetailsInput() {
    this.#dispatchCompletenessChange();

    if (this.#attributesSyncTimeout) clearTimeout(this.#attributesSyncTimeout);
    this.#attributesSyncTimeout = /** @type {any} */ (setTimeout(() => this.#syncAttributes(), 600));
  }

  /**
   * Buyer name/WhatsApp, recipient name/phone/address, delivery date, and delivery time slot
   * are all required. Date/slot fall back to whatever was already saved from a previous visit
   * (#persistedDate/#persistedSlot) when the customer hasn't reselected either in this
   * session — otherwise editing an unrelated field (e.g. fixing a typo in the phone number)
   * would incorrectly flip an already-complete order back to "incomplete".
   *
   * Crucially, that fallback only counts for the DATE if #persistedDateUsable is true — a
   * persisted date that's already in the past (an abandoned cart from a prior day) must NOT
   * satisfy this gate. Without that check, a customer returning after their saved date has
   * passed would find Checkout already enabled against a delivery date that's no longer
   * valid. The persisted SLOT has no equivalent past/future concept of its own (it's only
   * ever meaningful relative to whichever date ends up selected), so it's fine to trust
   * as-is once the date itself is genuinely usable.
   */
  #isComplete() {
    const { buyerNameInput, buyerWhatsappInput, recipientNameInput, recipientPhoneInput, addressInput } = this.refs;

    const buyerNameFilled = !!buyerNameInput?.value.trim();
    const buyerWhatsappFilled = !!buyerWhatsappInput?.value.trim();
    const nameFilled = !!recipientNameInput?.value.trim();
    const phoneFilled = !!recipientPhoneInput?.value.trim();
    const addressFilled = !!addressInput?.value.trim();
    const dateOk = !!this.#selected || this.#persistedDateUsable;
    const slotOk = !!this.#selectedSlot || !!this.#persistedSlot;

    return buyerNameFilled && buyerWhatsappFilled && nameFilled && phoneFilled && addressFilled && dateOk && slotOk;
  }

  #dispatchCompletenessChange() {
    this.dispatchEvent(
      new CustomEvent('delivery-scheduler:completeness-change', {
        bubbles: true,
        detail: { complete: this.#isComplete() },
      })
    );
  }

  /**
   * Writes every required field to cart attributes in one call — /cart/update.js replaces the
   * whole attributes object rather than merging, so this always sends the complete current
   * snapshot (falling back to #persistedDate/#persistedSlot for whichever of date/slot hasn't
   * been touched this session) rather than just whatever one field changed. Reads the cart's
   * *current* attributes first and spreads them underneath, so this doesn't wipe out unrelated
   * attribute keys written elsewhere on this same cart (e.g. assets/cart-addons.js's
   * "Add-on notes").
   */
  async #syncAttributes() {
    const { buyerNameInput, buyerWhatsappInput, recipientNameInput, recipientPhoneInput, addressInput } = this.refs;

    const attributes = {
      'Buyer Name': buyerNameInput?.value.trim() ?? '',
      'Buyer WhatsApp': buyerWhatsappInput?.value.trim() ?? '',
      'Recipient Name': recipientNameInput?.value.trim() ?? '',
      'Recipient Phone': recipientPhoneInput?.value.trim() ?? '',
      'Delivery Address': addressInput?.value.trim() ?? '',
      'Delivery Date': this.#selected ? this.#formatDate(this.#selected) : this.#persistedDate,
      'Delivery Time Slot': this.#selectedSlotLabel() || this.#persistedSlot,
    };

    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { ...cart.attributes, ...attributes } }),
      });
    } catch (error) {
      console.warn('[delivery-scheduler] Failed to sync cart attributes:', error);
    }
  }

  /** @param {CalendarDate} date */
  #formatDate(date) {
    const weekday = new Date(Date.UTC(date.year, date.month, date.day)).toLocaleDateString('en-US', {
      weekday: 'long',
      timeZone: 'UTC',
    });
    return `${weekday}, ${date.day} ${MONTH_LABELS[date.month]} ${date.year}`;
  }

  #selectedSlotLabel() {
    return SLOT_DEFS.find((slot) => slot.key === this.#selectedSlot)?.label ?? '';
  }

  #isAtCurrentMonth() {
    const now = getNairobiNow();
    return this.#viewYear === now.year && this.#viewMonth === now.month;
  }

  /**
   * @param {string} text
   * @param {boolean} showWhatsappLink
   */
  #showNotice(text, showWhatsappLink) {
    const { noticeBox, noticeText, noticeWaLink } = this.refs;
    if (!noticeBox || !noticeText) return;

    noticeText.textContent = text;
    noticeBox.hidden = false;
    if (noticeWaLink) noticeWaLink.hidden = !showWhatsappLink;
  }

  #hideNotice() {
    const { noticeBox } = this.refs;
    if (noticeBox) noticeBox.hidden = true;
  }

  #renderCalendar() {
    const { monthLabel, calendarGrid, prevMonthButton, selectedDateLabel } = this.refs;

    monthLabel.textContent = `${MONTH_LABELS[this.#viewMonth]} ${this.#viewYear}`;

    if (prevMonthButton instanceof HTMLButtonElement) {
      prevMonthButton.disabled = this.#isAtCurrentMonth();
    }

    const firstDow = firstWeekdayOfMonth(this.#viewYear, this.#viewMonth);
    const totalDays = daysInMonth(this.#viewYear, this.#viewMonth);

    let html = '';
    for (let i = 0; i < firstDow; i++) {
      html += '<span class="delivery-scheduler__day delivery-scheduler__day--blank" aria-hidden="true"></span>';
    }

    for (let day = 1; day <= totalDays; day++) {
      const cellDate = { year: this.#viewYear, month: this.#viewMonth, day };
      const isPast = compareDate(cellDate, this.#today) < 0;
      const isToday = compareDate(cellDate, this.#today) === 0;
      const isSat = isSaturday(this.#viewYear, this.#viewMonth, day);
      const blockedReason = getBlockedDateReason(this.#viewYear, this.#viewMonth, day);
      const isBlocked = blockedReason !== null;
      const todayClosed = isToday && this.#cutoffPassedToday;
      const disabled = isPast || isSat || isBlocked || todayClosed;
      const selected = !!this.#selected && compareDate(cellDate, this.#selected) === 0;

      let title = '';
      if (isSat) title = "We're closed on Saturdays";
      else if (isBlocked) title = BLOCKED_DATE_TITLES[blockedReason];
      else if (todayClosed) title = 'Same-day orders close at 4pm';

      const classes = [
        'delivery-scheduler__day',
        disabled ? 'delivery-scheduler__day--disabled' : '',
        isSat ? 'delivery-scheduler__day--saturday' : '',
        isBlocked ? 'delivery-scheduler__day--blocked' : '',
        selected ? 'delivery-scheduler__day--selected' : '',
      ]
        .filter(Boolean)
        .join(' ');

      html += `<button
        type="button"
        class="${classes}"
        data-cal-day="${day}"
        data-cal-month="${this.#viewMonth}"
        data-cal-year="${this.#viewYear}"
        data-cal-past="${isPast}"
        data-cal-saturday="${isSat}"
        data-cal-blocked-reason="${blockedReason ?? ''}"
        data-cal-today-closed="${todayClosed}"
        ${title ? `title="${title}"` : ''}
        aria-pressed="${selected}"
        on:click="/handleDayClick"
      >${day}</button>`;
    }

    calendarGrid.innerHTML = html;

    if (selectedDateLabel) {
      if (this.#selected) {
        const label = `${MONTH_LABELS[this.#selected.month]} ${this.#selected.day}, ${this.#selected.year}`;
        selectedDateLabel.textContent = `Selected: ${label}`;
        selectedDateLabel.hidden = false;
      } else {
        selectedDateLabel.hidden = true;
      }
    }
  }

  /**
   * Renders the four delivery time-slot buttons. A slot is only ever "Passed" when the
   * currently-selected calendar date is today — a future date's slots are always available
   * regardless of what time it is right now. "Now" is re-read fresh from Africa/Nairobi time
   * on every render (not cached from connectedCallback), so a slot that's still available when
   * the page loads correctly flips to "Passed" if the visitor lingers past its cutoff and then
   * reselects today (or the component re-renders for any other reason).
   */
  #renderSlots() {
    const { slotsGrid } = this.refs;
    if (!slotsGrid) return;

    const selectedIsToday = !!this.#selected && compareDate(this.#selected, this.#today) === 0;
    const now = getNairobiNow();
    const nowMinutes = now.hour * 60 + now.minute;

    // A previously-selected slot that's since become "Passed" (e.g. the visitor picked today's
    // 10am–12pm slot, then came back to this date after the cutoff) shouldn't stay selected.
    if (this.#selectedSlot && selectedIsToday) {
      const current = SLOT_DEFS.find((s) => s.key === this.#selectedSlot);
      if (current && nowMinutes >= current.cutoffMinutes) {
        this.#selectedSlot = '';
      }
    }

    let html = '';
    for (const slot of SLOT_DEFS) {
      const passed = selectedIsToday && nowMinutes >= slot.cutoffMinutes;
      const selected = !passed && this.#selectedSlot === slot.key;

      const classes = [
        'delivery-scheduler__slot',
        passed ? 'delivery-scheduler__slot--passed' : '',
        selected ? 'delivery-scheduler__slot--selected' : '',
      ]
        .filter(Boolean)
        .join(' ');

      html += `<button
        type="button"
        class="${classes}"
        data-slot-key="${slot.key}"
        data-slot-passed="${passed}"
        aria-pressed="${selected}"
        on:click="/handleSlotClick"
      >
        <span>${slot.label}</span>
        ${passed ? '<span class="delivery-scheduler__slot-passed-label">Passed</span>' : ''}
      </button>`;
    }

    slotsGrid.innerHTML = html;
  }
}

if (!customElements.get('delivery-scheduler-component')) {
  customElements.define('delivery-scheduler-component', DeliverySchedulerComponent);
}
